import { t } from '@lingui/macro'

import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import campaignApi from './campaign-api'
import organisationApi from './organisation-api'
import seriesApi from './series-api'
import { apiErrors } from '../../../shared/apiErrors'
import { notification } from '../components/common/notifications/Notifications'
import { transitionAppStateToLoggedOut } from '../pages/auth/SessionHelpers'
import { staticLinks } from '../../../shared/linkBuilder'
import { navigateTo } from '../app/util/appHistory'
import workspaceApi from './workspace-api'

export interface ILooopAxiosError<T, D = any> extends AxiosError<T, D> {
  config: AxiosRequestConfig & {
    throwErrors?: boolean
  }
}

axios.defaults.withCredentials = true
const config: AxiosRequestConfig = {
  headers: {
    Pragma: 'no-cache', // required to avoid caching of GETs on IE11
    'Cache-Control': 'no-cache',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
}

if (process.env.LOOOP_ENVIRONMENT === 'test') {
  const { JEST_WORKER_ID } = process.env
  const port = 3001 + Number(JEST_WORKER_ID)
  config.baseURL = `http://localhost:${port}` // otherwise baseURL is the same as window's
  const path = require('path') // a hack for because https://github.com/axios/axios/issues/1180
  const lib = path.join(
    path.dirname(require.resolve('axios')),
    'lib/adapters/http'
  )
  const http = require(lib)
  config.adapter = http
}
/* DEPRECATED
 * use graphQL instead
 */
export const axiosInstance = axios.create(config)

if (process.env.LOOOP_ENVIRONMENT !== 'test') {
  axiosInstance.interceptors.response.use(
    (res) => {
      return res
    },
    function (
      error: ILooopAxiosError<{
        message: string
        errorId: string
        response: any
      }>
    ) {
      console.error('error: ', error)
      if (error.config.throwErrors) {
        return Promise.reject(error)
      }

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        const { data, status } = error.response
        let { message } = data
        if (
          process.env.LOOOP_ENVIRONMENT === 'prod' &&
          (status === 500 || status === 503)
        ) {
          message = t`There was an API error. Please try again later or contact us. Error Id: ${data.errorId}`
          console.error(message)
        }
        notification.error(message)

        if (
          [
            apiErrors.ERR_SESSION_EXPIRED,
            apiErrors.ERR_NOT_LOGGED_IN,
            apiErrors.ERR_USER_ARCHIVED
          ].includes(data.message)
        ) {
          transitionAppStateToLoggedOut()
        } else if (data.message === apiErrors.ERR_ANALYTICS_NO_PERMISSION) {
          navigateTo(staticLinks.home)
        }
      }

      console.error('axios error response', error)
      if (error.message === 'Network Error') {
        notification.error(
          t`There was a network error. Contact our support if necessary.`
        )
      }
      return Promise.reject(error)
    }
  )
}

/* DEPRECATED
 * use graphQL instead
 */
const httpApi = {
  workspace: workspaceApi(axiosInstance),
  campaign: campaignApi(axiosInstance),
  organisation: organisationApi(axiosInstance),
  series: seriesApi(axiosInstance)
}

export default httpApi
